// src/pages/ProfilePage.tsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Button, Collapse, Divider, Paper, Stack, TextField, Typography,
} from '@mui/material';
import PersonRounded from '@mui/icons-material/PersonRounded';
import EmailRounded from '@mui/icons-material/EmailRounded';
import PhoneRounded from '@mui/icons-material/PhoneRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import LockResetRounded from '@mui/icons-material/LockResetRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import FlagRounded from '@mui/icons-material/FlagRounded';
import CreditCardRounded from '@mui/icons-material/CreditCardRounded';
import GroupRounded from '@mui/icons-material/GroupRounded';
import Swal from 'sweetalert2';
import PageHeader from '../components/ui/PageHeader';
import { getProfile, updateProfile, changePassword } from '../services/api';
import { setStoredUser } from '../utils/auth';

const TEAL = '#0F766E';
const TEAL_DARK = '#0B5F58';

const MANAGE: { title: string; text: string; path: string; icon: React.ComponentType<any> }[] = [
  { title: 'Expense From', text: 'Manage account expense-from values', path: '/manage/expensees', icon: ReceiptLongRounded },
  { title: 'Projects', text: 'Manage your projects', path: '/manage/projects', icon: FolderRounded },
  { title: 'Statuses', text: 'Manage status values', path: '/manage/statuses', icon: FlagRounded },
  { title: 'Payment types', text: 'Manage payment types', path: '/manage/payment-types', icon: CreditCardRounded },
  { title: 'Assignees', text: 'Manage assignee names', path: '/manage/assignees', icon: GroupRounded },
];

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '14px' },
  '& .MuiOutlinedInput-root fieldset': { borderWidth: 2, borderColor: `${TEAL}40` },
  '& .MuiOutlinedInput-root:hover fieldset': { borderColor: `${TEAL}99` },
  '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: TEAL },
  '& .MuiInputLabel-root.Mui-focused': { color: TEAL },
} as const;

type ProfileErrors = { fullName?: string; email?: string; mobile?: string };

export default function ProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({ fullName: '', email: '', mobile: '' });
  const [errors, setErrors] = useState<ProfileErrors>({});
  const [editing, setEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const passwordsMismatch = newPassword.length > 0 && confirmPassword.length > 0 && newPassword !== confirmPassword;

  useEffect(() => {
    (async () => {
      try {
        const data = await getProfile();
        setProfile({
          fullName: data.fullName || '',
          email: data.email || '',
          mobile: data.mobile || '',
        });
      } catch {
        Swal.fire('Error', 'Failed to load profile', 'error');
      }
    })();
  }, []);

  const handleUpdateProfile = async () => {
    const errs: ProfileErrors = {};
    if (!profile.fullName.trim()) errs.fullName = 'Full name is required';
    if (profile.email.trim() && !/^\S+@\S+\.\S+$/.test(profile.email.trim())) errs.email = 'Enter a valid email address';
    if (profile.mobile.trim() && !/^[0-9+()\-\s]{6,20}$/.test(profile.mobile.trim())) errs.mobile = 'Enter a valid mobile number';
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setLoading(true);
    try {
      const updated = await updateProfile(profile);
      setStoredUser(updated);
      setEditing(false);
      Swal.fire('Success', 'Profile updated successfully', 'success');
    } catch (e: any) {
      Swal.fire('Error', e?.response?.data?.message || 'Update failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      return Swal.fire('Error', 'Please fill all password fields', 'error');
    }
    if (newPassword.length < 6) {
      return Swal.fire('Error', 'New password should be at least 6 characters', 'error');
    }
    if (newPassword !== confirmPassword) {
      return Swal.fire('Error', 'New Password and Retype Password do not match', 'error');
    }

    setLoading(true);
    try {
      await changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPassword(false);
      Swal.fire('Success', 'Password changed successfully', 'success');
    } catch (e: any) {
      Swal.fire('Error', e?.response?.data?.message || 'Password change failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const initial = (profile.fullName.trim().charAt(0) || 'U').toUpperCase();

  const infoRows = [
    { icon: PersonRounded, label: 'Full Name', value: profile.fullName },
    { icon: EmailRounded, label: 'Email', value: profile.email },
    { icon: PhoneRounded, label: 'Mobile', value: profile.mobile },
  ];

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <PageHeader title="My Profile" subtitle="View and update your account details" />

      {/* ===== Summary card ===== */}
      <Paper elevation={0} className="qs-animate-in" sx={{ p: 2.5, mb: 2, borderRadius: '22px' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 64, height: 64, fontSize: 28, fontWeight: 900, color: TEAL,
              bgcolor: '#E6F4F1', border: `2px solid ${TEAL}33`,
            }}
          >
            {initial}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 900, fontSize: 20, lineHeight: 1.2 }} noWrap>{profile.fullName || '—'}</Typography>
            <Typography color="text.secondary" noWrap>{profile.email || '—'}</Typography>
            <Typography color="text.secondary" noWrap>{profile.mobile || '—'}</Typography>
          </Box>
        </Stack>
      </Paper>

      {/* ===== Details ===== */}
      <Paper elevation={0} className="qs-animate-in" sx={{ borderRadius: '22px', mb: 2.5, overflow: 'hidden', animationDelay: '.05s' }}>
        {infoRows.map((r, i) => {
          const Icon = r.icon;
          return (
            <Box key={r.label}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2.5, py: 1.75 }}>
                <Icon sx={{ color: '#374151' }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, color: TEAL, fontSize: 15 }}>{r.label}</Typography>
                  <Typography color="text.secondary" noWrap>{r.value || '—'}</Typography>
                </Box>
              </Stack>
              {i < infoRows.length - 1 && <Divider />}
            </Box>
          );
        })}
      </Paper>

      {/* ===== Actions ===== */}
      <Stack spacing={1.5} sx={{ mb: 2.5 }}>
        <Button
          fullWidth
          size="large"
          startIcon={<EditRounded />}
          onClick={() => { setEditing((v) => !v); setShowPassword(false); }}
          sx={{
            py: 1.4, borderRadius: '999px', fontWeight: 800, fontSize: 16, color: '#fff',
            background: `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})`,
            boxShadow: '0 10px 22px rgba(15,118,110,.35)',
            '&:hover': { transform: 'translateY(-2px)' },
          }}
        >
          {editing ? 'Close editor' : 'Edit Profile'}
        </Button>
        <Button
          fullWidth
          size="large"
          startIcon={<LockResetRounded />}
          onClick={() => { setShowPassword((v) => !v); setEditing(false); }}
          sx={{
            py: 1.3, borderRadius: '999px', fontWeight: 800, fontSize: 16, color: TEAL,
            border: `2px solid ${TEAL}`, bgcolor: 'background.paper',
            '&:hover': { bgcolor: `${TEAL}0F`, transform: 'translateY(-2px)' },
          }}
        >
          {showPassword ? 'Close' : 'Change Password'}
        </Button>
      </Stack>

      {/* ===== Edit profile ===== */}
      <Collapse in={editing} unmountOnExit>
        <Paper elevation={0} sx={{ p: 3, mb: 2.5, borderRadius: '22px' }}>
          <Typography sx={{ fontWeight: 900, fontSize: 18, color: TEAL, mb: 2 }}>Edit Profile</Typography>
          <Stack spacing={2}>
            <TextField
              label="Full Name"
              value={profile.fullName}
              onChange={(e) => { setProfile({ ...profile, fullName: e.target.value }); setErrors((p) => ({ ...p, fullName: undefined })); }}
              error={!!errors.fullName}
              helperText={errors.fullName}
              sx={fieldSx}
            />
            <TextField
              label="Email"
              value={profile.email}
              onChange={(e) => { setProfile({ ...profile, email: e.target.value }); setErrors((p) => ({ ...p, email: undefined })); }}
              error={!!errors.email}
              helperText={errors.email}
              sx={fieldSx}
            />
            <TextField
              label="Mobile"
              value={profile.mobile}
              onChange={(e) => { setProfile({ ...profile, mobile: e.target.value }); setErrors((p) => ({ ...p, mobile: undefined })); }}
              error={!!errors.mobile}
              helperText={errors.mobile}
              sx={fieldSx}
            />
            <Button
              onClick={handleUpdateProfile}
              disabled={loading}
              sx={{
                py: 1.3, borderRadius: '999px', fontWeight: 800, color: '#fff',
                background: `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})`,
                '&.Mui-disabled': { color: '#fff', opacity: 0.6 },
              }}
            >
              {loading ? 'Saving…' : 'Save Changes'}
            </Button>
          </Stack>
        </Paper>
      </Collapse>

      {/* ===== Change password ===== */}
      <Collapse in={showPassword} unmountOnExit>
        <Paper elevation={0} sx={{ p: 3, mb: 2.5, borderRadius: '22px' }}>
          <Typography sx={{ fontWeight: 900, fontSize: 18, color: TEAL, mb: 2 }}>Change Password</Typography>
          <Stack spacing={2}>
            <TextField
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              sx={fieldSx}
            />
            <TextField
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              helperText="Use at least 6 characters"
              sx={fieldSx}
            />
            <TextField
              type="password"
              label="Retype Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              error={passwordsMismatch}
              helperText={passwordsMismatch ? 'Passwords do not match' : ' '}
              sx={fieldSx}
            />
            <Button
              onClick={handleChangePassword}
              disabled={loading}
              sx={{
                py: 1.3, borderRadius: '999px', fontWeight: 800, color: '#fff',
                background: `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})`,
                '&.Mui-disabled': { color: '#fff', opacity: 0.6 },
              }}
            >
              {loading ? 'Updating…' : 'Update Password'}
            </Button>
          </Stack>
        </Paper>
      </Collapse>

      {/* ===== Manage ===== */}
      <Typography sx={{ fontWeight: 900, fontSize: 16, mb: 1, mt: 1 }}>Manage</Typography>
      <Paper elevation={0} className="qs-animate-in" sx={{ borderRadius: '22px', overflow: 'hidden', animationDelay: '.1s' }}>
        {MANAGE.map((m, i) => {
          const Icon = m.icon;
          return (
            <Box key={m.path}>
              <Box
                role="link"
                tabIndex={0}
                onClick={() => navigate(m.path)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(m.path); } }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.75, cursor: 'pointer', outline: 'none',
                  transition: 'background-color .15s ease',
                  '&:hover, &:focus-visible': { bgcolor: `${TEAL}0F` },
                }}
              >
                <Icon sx={{ color: '#374151' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800 }}>{m.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{m.text}</Typography>
                </Box>
                <ChevronRightRounded sx={{ color: 'text.secondary' }} />
              </Box>
              {i < MANAGE.length - 1 && <Divider />}
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
}
